import { Button } from 'blaze-ui/Button'
import Styles from './TopBar.module.scss'
import { RootState } from 'types'
import { useSelector } from 'react-redux'
import { Search } from 'blaze-ui/icons'
import { NotificationsTrayContainer } from 'blaze-components/NotificationsTray/NotificationsTrayContainer'
import { useWorkspaces } from 'hooks/useWorkspaces'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useModals } from 'almanac-ui/Modal'
import { SearchModal } from 'screens/blaze/Search/SearchModal/SearchModal'
import { MobileTopBar } from './MobileTopBar/MobileTopBar'
import cx from 'classnames'
import { ProfileDropdown } from './ProfileDropdown/ProfileDropdown'
import { VirtualMarketerUpgradeButton } from 'blaze-components/VirtualMarketerUpgradeButton/VirtualMarketerUpgradeButton'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { AutopilotProUpgradeButton } from 'components/AutopilotUpgradeButtons'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'
import { Heading } from 'blaze-ui/Heading/Heading'
import { useRootStore } from 'stores/RootStore'
import { observer } from 'mobx-react-lite'
import { CreditUsageDropdown } from 'blaze-components/CreditUsageDropdown/CreditUsageDropdown'
import { ReactNode } from 'react'
import { useAuth } from 'hooks/UseAuth'

interface Props {
  pageName?: string | ReactNode
  distributeEvenly?: boolean
  isLockable?: boolean
  leftContent?: ReactNode
  centerContent?: ReactNode
  rightContent?: ReactNode
}

export const TopBar = observer(
  ({ isLockable = true, pageName, leftContent, centerContent, rightContent, distributeEvenly = false }: Props) => {
    const sidebarStore = useRootStore().leftSidebarStore
    const isSystemRedesignEnabled = useIsSystemRedesign()
    const currentWorkspaceId = useCurrentWorkspaceId()
    const { isMobile } = useResponsiveDesign()
    const workspaces = useWorkspaces()
    const { sidebarLocked } = useSelector(({ sidebar: { locked: sidebarLocked } }: RootState) => ({ sidebarLocked }))
    const { openModal } = useModals()
    const currentWorkspace = useCurrentWorkspace()
    const auth = useAuth()

    const style = isSystemRedesignEnabled ? { width: `calc(100% - ${sidebarStore.sidebarWidth}px)` } : {}

    if (isMobile) return <MobileTopBar />

    return (
      <div
        className={cx(Styles.topBarContainer, {
          [Styles.sidebarLocked]: sidebarLocked && isLockable,
          [Styles.distributeEvenly]: distributeEvenly,
        })}
        style={style}
        data-testid="top-bar"
      >
        <div className={Styles.leftButtons}>
          {isSystemRedesignEnabled ? (
            <>
              {typeof pageName === 'string' ? (
                <Heading level={5} lineClamp={1}>
                  {pageName}
                </Heading>
              ) : (
                pageName
              )}
            </>
          ) : (
            <Button variant="tertiary" size="lg" frontIcon={Search} onPress={() => openModal(SearchModal)} rounded>
              Search
            </Button>
          )}
          {leftContent}
        </div>
        {isSystemRedesignEnabled && <div className={Styles.centerContent}>{centerContent}</div>}
        <div className={Styles.rightButtons}>
          {rightContent}
          {currentWorkspace?.is_ap_freemium ? (
            <AutopilotProUpgradeButton variant="upgrade" className={Styles.upgradeButton} />
          ) : (
            <VirtualMarketerUpgradeButton />
          )}
          <CreditUsageDropdown />
          {!isSystemRedesignEnabled && <NotificationsTrayContainer />}
          {auth && <ProfileDropdown selectedWorkspaceId={currentWorkspaceId} workspaces={workspaces} />}
        </div>
      </div>
    )
  },
)
